/**
 * KnowledgeFilterPanel — slide-in filter panel for the KnowledgeObjectDetailPage.
 *
 * Provides:
 *  - Layer checkboxes: L1 / L2 / L3
 *  - Unit-type checkboxes: all KnowledgeUnitType variants
 *  - L3 extraction kind checkboxes (only shown when L3 is active)
 *  - Search input (client-side, filters content + label)
 *  - "Clear all" reset
 *
 * The panel is designed to be extended with a "Graph view" tab in the future.
 */
import { Checkbox, Search, Button, Tag } from '@carbon/react';
import { Close, Filter } from '@carbon/icons-react';
import type { KnowledgeUnitType } from '@shared/types/knowledge-base';
import './KnowledgeFilterPanel.scss';

export type ExtractionKind =
  | 'key_claims'
  | 'entity_candidates'
  | 'table_structured'
  | 'molecule_smile'
  | 'named_entities';

export interface KnowledgeFilters {
  search: string;
  layers: Set<'L1' | 'L2' | 'L3'>;
  types: Set<KnowledgeUnitType>;
  /** When non-null, only show L3 units whose extraction matches one of these kinds. null = all kinds */
  extractionKinds: Set<ExtractionKind> | null;
}

export const ALL_LAYERS: Array<'L1' | 'L2' | 'L3'> = ['L1', 'L2', 'L3'];

export const ALL_UNIT_TYPES: KnowledgeUnitType[] = [
  'text',
  'heading',
  'table',
  'image',
  'code',
  'column',
  'variable',
  'record',
];

export const ALL_EXTRACTION_KINDS: ExtractionKind[] = [
  'key_claims',
  'entity_candidates',
  'table_structured',
  'molecule_smile',
  'named_entities',
];

const EXTRACTION_KIND_LABELS: Record<ExtractionKind, string> = {
  key_claims: 'Key claims',
  entity_candidates: 'Entities',
  table_structured: 'Structured table',
  molecule_smile: 'Molecule (SMILES)',
  named_entities: 'Named entities',
};

export function defaultFilters(): KnowledgeFilters {
  return {
    search: '',
    layers: new Set(ALL_LAYERS),
    types: new Set(ALL_UNIT_TYPES),
    extractionKinds: null,
  };
}

interface KnowledgeFilterPanelProps {
  open: boolean;
  filters: KnowledgeFilters;
  onChange: (filters: KnowledgeFilters) => void;
  onClose: () => void;
  /** Total unit count — displayed in the header */
  totalCount: number;
  /** Visible unit count after filters — displayed below the header */
  filteredCount: number;
}

const LAYER_LABELS: Record<'L1' | 'L2' | 'L3', string> = {
  L1: 'L1 — Raw chunks',
  L2: 'L2 — Summaries',
  L3: 'L3 — Extractions',
};

// Unit-type colors are applied via CSS data-type attributes on .kf-panel__type-dot

export default function KnowledgeFilterPanel({
  open,
  filters,
  onChange,
  onClose,
  totalCount,
  filteredCount,
}: KnowledgeFilterPanelProps) {
  if (!open) return null;

  const isFiltered = filteredCount !== totalCount;

  function toggleLayer(layer: 'L1' | 'L2' | 'L3') {
    const next = new Set(filters.layers);
    if (next.has(layer)) next.delete(layer);
    else next.add(layer);
    onChange({ ...filters, layers: next });
  }

  function toggleType(type: KnowledgeUnitType) {
    const next = new Set(filters.types);
    if (next.has(type)) next.delete(type);
    else next.add(type);
    onChange({ ...filters, types: next });
  }

  function toggleExtractionKind(kind: ExtractionKind) {
    // If currently null (= all), start a set with all kinds minus this one
    const current = filters.extractionKinds ?? new Set(ALL_EXTRACTION_KINDS);
    const next = new Set(current);
    if (next.has(kind)) {
      next.delete(kind);
      // If empty → treat as "none selected" (null = all to avoid edge case)
      onChange({ ...filters, extractionKinds: next.size === 0 ? null : next });
    } else {
      next.add(kind);
      // If all are selected → reset to null (= all)
      onChange({ ...filters, extractionKinds: next.size === ALL_EXTRACTION_KINDS.length ? null : next });
    }
  }

  const showExtractionFilter = filters.layers.has('L3');

  function clearAll() {
    onChange(defaultFilters());
  }

  return (
    <aside className="kf-panel">
      {/* ── Panel header ──────────────────────────────────────────────── */}
      <div className="kf-panel__header">
        <Filter size={16} />
        <span className="kf-panel__title">Filters</span>
        {isFiltered && (
          <Tag type="blue" size="sm" className="kf-panel__active-tag">
            {filteredCount} / {totalCount}
          </Tag>
        )}
        <span style={{ flex: 1 }} />
        <button className="kf-panel__close" onClick={onClose} aria-label="Close filter panel">
          <Close size={16} />
        </button>
      </div>

      {/* ── Search ────────────────────────────────────────────────────── */}
      <div className="kf-panel__section">
        <Search
          size="sm"
          labelText="Search knowledge units"
          placeholder="Search content, labels…"
          value={filters.search}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
        />
      </div>

      {/* ── Layer filter ──────────────────────────────────────────────── */}
      <div className="kf-panel__section">
        <p className="kf-panel__section-title">Layer</p>
        {ALL_LAYERS.map((layer) => (
          <Checkbox
            key={layer}
            id={`filter-layer-${layer}`}
            labelText={LAYER_LABELS[layer]}
            checked={filters.layers.has(layer)}
            onChange={() => toggleLayer(layer)}
          />
        ))}
      </div>

      {/* ── Unit type filter ──────────────────────────────────────────── */}
      <div className="kf-panel__section">
        <p className="kf-panel__section-title">Unit type</p>
        <div className="kf-panel__type-grid">
          {ALL_UNIT_TYPES.map((type) => (
            <button
              key={type}
              className={`kf-panel__type-chip${filters.types.has(type) ? ' kf-panel__type-chip--active' : ''}`}
              onClick={() => toggleType(type)}
              aria-pressed={filters.types.has(type)}
            >
              <span
                className="kf-panel__type-dot"
                data-type={type}
              />
              {type}
            </button>
          ))}
        </div>
      </div>

      {/* ── L3 extraction kind filter (only when L3 is active) ────────── */}
      {showExtractionFilter && (
        <div className="kf-panel__section">
          <p className="kf-panel__section-title">
            L3 extraction type
            {filters.extractionKinds !== null && (
              <button
                className="kf-panel__section-reset"
                onClick={() => onChange({ ...filters, extractionKinds: null })}
              >
                reset
              </button>
            )}
          </p>
          <div className="kf-panel__type-grid">
            {ALL_EXTRACTION_KINDS.map((kind) => {
              const active = filters.extractionKinds === null || filters.extractionKinds.has(kind);
              return (
                <button
                  key={kind}
                  className={`kf-panel__type-chip kf-panel__type-chip--ext${active ? ' kf-panel__type-chip--active' : ''}`}
                  onClick={() => toggleExtractionKind(kind)}
                  aria-pressed={active}
                  title={EXTRACTION_KIND_LABELS[kind]}
                >
                  {EXTRACTION_KIND_LABELS[kind]}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Future: graph view placeholder ───────────────────────────── */}
      <div className="kf-panel__section kf-panel__section--future">
        <p className="kf-panel__section-title">View</p>
        <p className="kf-panel__future-note">
          📊 Graph view — coming soon
        </p>
      </div>

      {/* ── Clear all ─────────────────────────────────────────────────── */}
      <div className="kf-panel__footer">
        <Button kind="ghost" size="sm" onClick={clearAll} disabled={!isFiltered}>
          Clear all filters
        </Button>
      </div>
    </aside>
  );
}

// Made with Bob
